import * as React from 'react';
import './dataSourceModal.scss';
import { Modal } from 'rcd-cpp-ui/ui/modal/Modal';
import { Buttons, Primary, Secondary } from 'rcd-cpp-ui/ui/button';
import { useActions } from './dataSourceModalActions';
import { Metadata } from '../../../types';
import { useLoader } from '../../Loader/loader';
import { useAlert } from '../../Alert/alert';
import { getToken } from '../../../utils/authClient';
import { useSelector } from '../../../utils/useSelector';
import { NodeType } from '../../../enums/NodeType';
import { DataSource } from '../../../enums/DataSource';
import { dataSourceItemField } from './dataSourceField';
import { FormUpdater } from '../../../types/Field';

type Props = {
  data: Partial<Metadata>;
  updater?: FormUpdater;
  onSave: () => void;
  onClose: () => any;
  saveDsAndProjects: (
    dataSourceList: Metadata[],
    projectList: Metadata[],
    dataSourceItemList: Metadata[]
  ) => any;
};

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'cpp-item-data-sources': any;
    }
  }
}
export const DataSourceModal = (p: Props) => {
  const getAPI = useSelector(s => s.app.elFn.getAPI);
  const { updater, saveDsAndProjects } = p;
  const { updateFields } = updater;
  const { onClose } = p;
  const [hideButtons, setHideButtons] = React.useState(false);
  const [disableResetBtn, setDisableResetBtn] = React.useState(true);
  const actions = useAlert({ ...useActions(), onSave: p.onSave });
  const { isLoading } = useLoader(useAlert(actions));
  //to do: after deploying types:
  //const cppDataSources = React.useRef<CPPElements.ItemDataSources>(null);
  const cppDataSources = React.useRef<any>(null);
  const schema = useSelector(s => s.form?.schema);
  const fields = schema?.props?.fields;

  const dataSourceField = !!fields?.filter(f => f?.key == 'Item.dataSource')
    ?.length
    ? fields?.filter(f => f?.key == 'Item.dataSource')[0]
    : null;
  const projectField = !!fields?.filter(f => f?.key == 'Item.project')?.length
    ? fields?.filter(f => f?.key == 'Item.project')[0]
    : null;

  const dataSources = p?.data?.props?.dataSource;
  const projects = p?.data?.props?.project;
  const dataSourceItems = p?.data?.props?.dataSourceItem;
  const itemId = p?.data?.props?.itemId;

  const selectedDataSourceProjects = dataSources?.map(ds => {
    if (ds?.props?.id == DataSource.PublishedGartnerResearch) {
      return {
        dataSourceId: ds.props.id,
        name: ds.props.name,
        projects: dataSourceItems?.map(p => {
          return {
            externalId: p.props.itemId,
            projectId: p.props.itemId,
            title: p.props.title,
            dataSourceId: ds.props.id
          };
        })
      };
    }
    const dataSourceProjects = projects?.filter(
      p => p?.props?.dataSourceId === ds?.props?.id
    );
    if (dataSourceProjects && dataSourceProjects?.length > 0) {
      return {
        dataSourceId: ds.props.id,
        name: ds.props.name,
        projects: dataSourceProjects.map(p => {
          return {
            externalId: p.props.id,
            projectId: p.props.projectId,
            title: p.props.name,
            dataSourceId: p.props.dataSourceId
          };
        })
      };
    } else {
      return {
        dataSourceId: ds.props.id,
        name: ds.props.name
      };
    }
  });

  const onStateChange = payload => {
    payload?.projectSearch == true
      ? setHideButtons(true)
      : setHideButtons(false);
    payload?.disableResetBtn == true
      ? setDisableResetBtn(true)
      : setDisableResetBtn(false);
  };

  const onDone = async () => {
    try {
      const validateErrors = cppDataSources.current.validateDataSources();
      let selectedDataSources;
      if (!validateErrors) {
        selectedDataSources = cppDataSources.current.getSelectedDataSources();
        onClose();

        const dataSourceList = [];
        const projectList = [];
        const dataSourceItemList = [];

        if (selectedDataSources && selectedDataSources.length > 0) {
          for (const selectedDataSource of selectedDataSources) {
            const dataSource = {
              labels: [NodeType.DataSource],
              props: {
                name: selectedDataSource.name,
                id: selectedDataSource.dataSourceId,
                refId: 'ds:' + selectedDataSource.dataSourceId
              }
            };
            dataSourceList.push(dataSource);

            if (
              selectedDataSource.projects &&
              selectedDataSource.projects.length > 0
            ) {
              for (const selectedProject of selectedDataSource.projects) {
                if (
                  dataSource.props.id == DataSource.PublishedGartnerResearch
                ) {
                  const existed = dataSourceItems?.find(
                    i => i?.props?.itemId == selectedProject.externalId
                  );
                  if (existed) dataSourceItemList.push(existed);
                  else
                    dataSourceItemList.push({
                      labels: [NodeType.Item],
                      props: {
                        itemId: selectedProject.externalId,
                        title: selectedProject.title
                      }
                    });
                } else {
                  const project = {
                    labels: [NodeType.Project],
                    props: {
                      name: selectedProject.title,
                      id: selectedProject.externalId,
                      refId: 'prj:' + selectedProject.externalId,
                      dataSourceId: selectedProject.dataSourceId,
                      projectId: selectedProject.projectId
                    }
                  };
                  projectList.push(project);
                }
              }
            }
          }
        }
        const itemIds = dataSourceItemList
          .map(i => (!i.props?.refId ? parseInt(i.props.itemId) : null))
          .filter(x => !!x && !isNaN(x));
        if (itemIds.length > 0) {
          const { searchReferences } = getAPI();
          const items = await searchReferences({
            nodeType: NodeType.Item,
            itemIds
          });
          dataSourceItemList.forEach(i => {
            if (!i.props?.refId) {
              const item = items.find(x => x.props.itemId == i.props.itemId);
              if (item) {
                i.props.refId = item.props.refId;
              }
            }
            return i;
          });
        }
        saveDsAndProjects(
          dataSourceList,
          projectList,
          dataSourceItemList.filter(x => !!x.props?.refId)
        );
      }
    } catch (e) {
      console.error('Failed to save data sources and projects with error ', e);
      updateFields([
        { field: dataSourceField, value: dataSources },
        { field: projectField, value: projects },
        { field: dataSourceItemField, value: dataSourceItems }
      ]);
    }
  };

  const onReset = () => {
    cppDataSources.current.resetDataSources();
  };

  React.useEffect(() => {
    if (cppDataSources.current) {
      cppDataSources.current.getToken = getToken;
      cppDataSources.current.onDone = onDone as any;
      cppDataSources.current.onCancel = onClose;
      cppDataSources.current.onStateChange = onStateChange;
    }
  }, []);

  return (
    <div className="slider-activator">
      <Modal
        id="dataSourceModal"
        onClose={onClose}
        header={'Data Sources'}
        footer={
          !hideButtons && (
            <Buttons>
              <Secondary
                label="Reset"
                onClick={onReset}
                disabled={disableResetBtn}
              />
              <Secondary label="Cancel" onClick={onClose} />
              <Primary
                label="Save"
                onClick={async () => {
                  onDone();
                }}
                disabled={isLoading}
              />
            </Buttons>
          )
        }
      >
        <cpp-item-data-sources
          ref={cppDataSources}
          id="cppDataSources"
          itemId={itemId}
          onCancel={onClose}
          onDone={onDone}
          onStateChange={onStateChange}
          onSearchFactBase={() => {}}
          getToken={getToken}
          selectedDataSources={JSON.stringify(selectedDataSourceProjects)}
        ></cpp-item-data-sources>
      </Modal>
    </div>
  );
};
